import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  captureAction,
  captureAppealDraft,
  captureAuditRisk,
  captureCodeEnroll,
  captureConsentMissing,
  captureConsentSendLink,
  captureContractRates,
  captureDenials,
  captureEasiestWins,
  captureForecast,
  capturePaidTrend,
  captureQualification,
  captureReadiness,
  captureShareAgreements,
  captureSummaryV3,
  shownHonesty,
  customerMark,
  captureBatchResend,
  captureEvents,
  capturePractitionerAction,
  capturePractitioners,
  captureSendForSignature,
  clearinghouseBatches,
  codeAccessRequest,
  codeAccessRequests,
  feeSchedule,
  type CaptureActionKey,
  type CaptureDenial,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { useDemoBuilding } from '../../session/DemoScope';
import { useOpenHelp, usePageTab } from '../../shell/Shell';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { ClaimsToSign } from '../../ui/ClaimsToSign';
import { MoreMenu } from '../../ui/MoreMenu';
import { tierLabel } from '../residents/common';
import { PRAC_ACTIONS, withBatches, withPractitioners } from './extras';
import { Contracts } from './Contracts';
import { GenTab, type Tag } from './GenTab';
import { Snapshot, type SnapKpi, type TodayItem } from './Snapshot';
import { auditPack, denials, forecast, network, opportunity, oppGroups, readiness, readyRows, type GenUi, type OppGroup, type ReadyRow } from './gen';
import { useBuildLabels } from '../../ui/buildLabels';
import { PRI, askChip, csvLine, download, moneyC, needsBuilding, pre, pvTab, scopeChip, usd0, usd2, useCaptureApi, type Honesty } from './lib';
import { PAYER_NAME, PERIOD_MONTHS, daysToPay, delta, lastTwo, lensRows, monthTotals, notBilled, type Lens, type LensRow, type PayerPick, type Period } from './model';

export type CaptureTab = 'Snapshot' | 'Opportunity' | 'Contracts' | 'Forecast' | 'Readiness' | 'Denials & Underpayments' | 'Audit Pack';
export const TABS: CaptureTab[] = ['Snapshot', 'Opportunity', 'Contracts', 'Forecast', 'Readiness', 'Denials & Underpayments', 'Audit Pack'];
/** The tabs used every day stay in the row (V9 capture.js DAILY); the rest sit under More. */
export const DAILY: CaptureTab[] = ['Snapshot', 'Readiness', 'Denials & Underpayments'];
const TAB_C: Partial<Record<CaptureTab, string>> = { Opportunity: '#2F5C97', Contracts: '#B3261E', Readiness: '#6B4A12', 'Denials & Underpayments': '#B3261E' };
type Scope = 'bld' | 'port' | 'net';
/** The rate grid's codes, as the design lays them out (capture.js CODES). */
const CONTRACT_CODES = '99490,99439,99487,G0557,G0558,99454,99457';
type AskKey = 'blk' | 'ren' | 'apl';

/**
 * Capture (Console V7, capture.js): Snapshot, Opportunity, Contracts (This building,
 * Portfolio, Network), Forecast, Readiness, Denials & Underpayments and Audit Pack, with
 * the three Ask Atlas money questions. Super Admin and Operator see every facility and
 * follow the facility switcher; a Facility Admin sees their own building (the server pins
 * it); the Partner reads and acts on nothing. Aithora never submits a claim: the enrolled
 * practitioner bills, and the building's share comes only from an agreement on file.
 */
export function CapturePage() {
  const { role, realRole } = useConsoleRole();
  const { client } = useSession();
  // Figure labels (MEASURED, MODELED, ESTIMATED, PUBLISHED, SAMPLE) show to Developer and
  // Super Admin. Everyone else, Facility Admin included, sees a quiet "est." after a modeled or
  // estimated number and nothing else (Andrew 2026-10-15), so a modeled dollar is never taken
  // for a measured one. SAMPLE drops inside a seeded demo building, where the banner says so.
  const demo = useDemoBuilding();
  const buildLabels = useBuildLabels();
  const shown = <L extends string>(l: L | null): L | null => shownHonesty(l, demo);
  const suffix = (w: string) => (demo && w === 'SAMPLE' ? '' : ' · ' + w);
  const openHelp = useOpenHelp();
  const roReason = useReadOnlyReason();
  const fa = role === 'ADMIN';
  const operator = role === 'SUPER_ADMIN' || role === 'OPERATOR';
  const canAct = (operator || fa) && roReason === null && realRole !== 'PARTNER';
  const writeOff = canAct ? null : roReason ?? 'Read only: your account can look, not change.';

  const [tab, setTab] = useState<CaptureTab>('Snapshot');
  const [cscope, setCscope] = useState<Scope>('bld');
  const [ask, setAsk] = useState<AskKey | null>(null);
  const [lens, setLens] = useState<Lens>('code');
  const [pt, setPt] = useState<PayerPick>('All');
  const [per, setPer] = useState<Period>('Month');
  const [gf, setGf] = useState<Record<string, string>>({});
  const [gsel, setGsel] = useState<Record<string, string | null>>({});
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [railOpen, setRailOpen] = useState(false);
  const [scenario, setScenario] = useState<string | null>(null);
  const [uptake, setUptake] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1180);
  usePageTab(tab);

  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 1180);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  useEffect(() => setRailOpen(false), [tab, cscope]);

  // Scope: a Facility Admin's building comes from the session; Aithora staff follow the switcher.
  const pick = usePickedIds();
  const pickOne = pick && pick.length === 1 ? pick[0] : undefined;
  const facility = fa ? undefined : pickOne;
  const facilities = fa || pickOne ? undefined : facilitiesParam(pick);
  const oneBuilding = fa || !!pickOne;
  const effScope: Scope = operator ? cscope : 'bld';

  const summary = useCaptureApi((c, signal) => captureSummaryV3(c, { query: { facility, facilities }, signal }), [facility, facilities]);
  // Which buildings every figure here covers, said on the page (a Partner read Finance on one
  // building beside Capture's portfolio with nothing to tell them apart).
  const { inView } = useFacilityScope();
  const scopeWords = fa
    ? null
    : pickOne
      ? (inView.find((f) => f.id === pickOne)?.name ?? 'One building')
      : (() => {
          const n = inView.length || (summary.data?.facilities ?? 0);
          return n ? 'All ' + n + (n === 1 ? ' building' : ' buildings') + ' in view' : 'every building';
        })();
  const trend = useCaptureApi((c, signal) => capturePaidTrend(c, { query: { facility, facilities }, signal }), [facility, facilities]);
  const fee = useCaptureApi((c, signal) => feeSchedule(c, { signal }), []);
  const agreements = useCaptureApi((c, signal) => captureShareAgreements(c, { query: { facility }, signal }), [facility], oneBuilding);
  const ready = useCaptureApi((c, signal) => captureReadiness(c, { query: { facility }, signal }), [facility], !facilities);
  const consent = useCaptureApi((c, signal) => captureConsentMissing(c, { query: { facility }, signal }), [facility], !facilities);
  const qual = useCaptureApi((c, signal) => captureQualification(c, { query: { facility }, signal }), [facility], oneBuilding);
  const wins = useCaptureApi((c, signal) => captureEasiestWins(c, { query: { facility }, signal }), [facility], oneBuilding);
  const den = useCaptureApi((c, signal) => captureDenials(c, { query: { facility }, signal }), [facility], oneBuilding);
  const rates = useCaptureApi((c, signal) => captureContractRates(c, { query: { facility, codes: CONTRACT_CODES }, signal }), [facility], oneBuilding && (tab === 'Contracts' || tab === 'Snapshot'));
  const batches = useCaptureApi((c, signal) => clearinghouseBatches(c, { query: { facility }, signal }), [facility], tab === 'Readiness' && !facilities);
  const pracs = useCaptureApi((c, signal) => capturePractitioners(c, { query: { facility }, signal }), [facility], tab === 'Opportunity' && !facilities);
  const events = useCaptureApi((c, signal) => captureEvents(c, { query: { facility, limit: 20 }, signal }), [facility], !facilities);
  const masked = !!(wins.data?.masked || summary.data?.masked || rates.data?.masked);
  const accessReqs = useCaptureApi((c, signal) => codeAccessRequests(c, { query: { status: 'pending' }, signal }), [], fa && masked && (tab === 'Opportunity' || tab === 'Contracts'));
  const [reqSent, setReqSent] = useState(false);
  const [signSent, setSignSent] = useState<number | null>(null);
  // The LIVE line moves every 4.5 s (V6 _capTimer), only while the tab is visible.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => { if (!document.hidden) setTick((t) => t + 1); }, 4500);
    return () => window.clearInterval(id);
  }, []);
  const audit = useCaptureApi((c, signal) => captureAuditRisk(c, { query: { facility }, signal }), [facility], oneBuilding && tab === 'Audit Pack');
  const fcQuery = { facility, scenario: (scenario ?? undefined) as 'cautious' | 'expected' | 'strong' | undefined, uptake: uptake != null ? uptake / 100 : undefined };
  const fc = useCaptureApi((c, signal) => captureForecast(c, { query: fcQuery, signal }), [facility, scenario, uptake], tab === 'Forecast');
  const fcBase = useCaptureApi((c, signal) => captureForecast(c, { query: { facility }, signal }), [facility], tab === 'Forecast');
  const denSel = gsel['Denials & Underpayments'] ?? den.data?.denials[0]?.id ?? null;
  const draft = useCaptureApi((c, signal) => captureAppealDraft(c, { params: { id: denSel ?? '' }, query: { facility }, signal }), [denSel, facility], tab === 'Denials & Underpayments' && !!denSel && !!den.data?.denials.find((d) => d.id === denSel)?.appealable);
  useEffect(() => setEditDraft(null), [denSel]);

  const tag: Tag = (b) => (!buildLabels ? (customerMark(b) as Honesty | null) : b === 'PUBLISHED' ? 'PUBLISHED' : (shown(b ?? null) as Honesty | null));
  const now = Date.now();
  const pageSample = summary.data?.real === false || trend.data?.real === false;
  const tagSuffix = pageSample && buildLabels ? suffix('SAMPLE') : '';
  const measured: Honesty | null = trend.data ? (trend.data.real ? 'MEASURED' : 'SAMPLE') : null;
  const modeled: Honesty | null = summary.data ? (summary.data.real ? 'MODELED' : 'SAMPLE') : null;
  const CHOOSE = 'Choose one building in the facility switcher to see this.';

  // ── shared derivations ─────────────────────────────────────────────────────
  const rRows = useMemo(() => readyRows(ready.data, qual.data, now), [ready.data, qual.data, now]);
  const blocked = rRows.filter((r) => r.at < 5);
  const groups = useMemo(() => oppGroups(wins.data), [wins.data]);
  const consentRows = consent.data?.residents ?? [];
  const sendable = consentRows.filter((r) => r.action === 'send_family_link');
  const denialList = den.data?.denials ?? [];
  const nearDenial = denialList.filter((d) => d.appealable && d.daysLeft != null && d.daysLeft >= 0).sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))[0] ?? null;

  const flash = (m: string) => {
    setNote(m);
    window.setTimeout(() => setNote((x) => (x === m ? null : x)), 6000);
  };
  const failed = (e: unknown) => flash(e instanceof Error ? e.message : 'That did not go through.');
  const goTab = (t: CaptureTab, sel?: string | null) => {
    setTab(t);
    if (sel !== undefined) setGsel((s) => ({ ...s, [t]: sel }));
  };
  const sendLinks = async (ids: string[], key: string) => {
    if (!ids.length) return flash('No family contact is on file for a consent link. Send the first link from the resident page.');
    let ok = 0;
    for (const id of ids) {
      try {
        const r = await captureConsentSendLink(client, { params: { residentId: id }, body: {} });
        if (r.sent) ok += 1;
      } catch (e) {
        failed(e);
      }
    }
    if (ok) {
      setDone((d) => ({ ...d, [key]: true }));
      flash(ok + ' consent link' + (ok === 1 ? '' : 's') + ' sent');
      consent.reload();
    }
  };

  // ── Snapshot ───────────────────────────────────────────────────────────────
  const rows = useMemo(
    () => lensRows(lens, trend.data, { pt, per, summary: summary.data, fee: fee.data, agreements: agreements.data?.agreements }),
    [lens, trend.data, pt, per, summary.data, fee.data, agreements.data],
  );
  const mt = monthTotals(trend.data);
  const n = PERIOD_MONTHS[per];
  const [billCur, billPrev] = lastTwo(mt.billed, n);
  const [unmatchedCur] = lastTwo(mt.unmatched, n);
  const shareWin = mt.share.slice(-n);
  const shareKnown = shareWin.length > 0 && shareWin.every((v) => v != null);
  const shareCur = shareWin.reduce<number>((s, v) => s + (v ?? 0), 0);
  const sharePrev = mt.share.slice(-2 * n, -n).reduce<number>((s, v) => s + (v ?? 0), 0);
  const claimsIn = (from: number, to?: number) => {
    const ms = new Set((trend.data?.months ?? []).slice(from, to));
    const rs = (trend.data?.rows ?? []).filter((r) => ms.has(r.period));
    const p = rs.reduce((s, r) => s + r.paidClaims, 0);
    const d = rs.reduce((s, r) => s + r.deniedClaims, 0);
    return p + d ? (d / (p + d)) * 100 : null;
  };
  const denCur = claimsIn(-n);
  const denPrev = claimsIn(-2 * n, -n);
  const dBill = delta(billCur, billPrev);
  const dShare = delta(shareCur, sharePrev);
  const s = summary.data;
  // A building with no claims back yet: plain words in place of figures, never $0.
  const noClaims = !!trend.data && !trend.data.real;
  const NO_CLAIMS = 'No claims yet';
  // Days to pay over at least the last three months (one month is too few claims to read).
  const dtp = daysToPay(trend.data, Math.max(3, n));
  const dDtp = dtp.cur != null && dtp.prev != null ? Math.round(dtp.cur - dtp.prev) : null;
  const perWord = per === '12 mo' ? '12 months' : per.toLowerCase();
  const nAgree = agreements.data?.agreements.length ?? 0;
  // Not billed: care delivered with no claim, minus billing on unmatched claims only (a matched
  // claim's code is already out of the measured care; AF-1668). No claims: all of it.
  // With no documented care, the summary's modeled opportunity (profile qualifies, no proof yet).
  const notBilledKpi: { value: number | null; basis: 'measured' | 'modeled' } =
    s?.measured.value != null && trend.data
      ? { value: notBilled(s.measured.value * n, unmatchedCur, null), basis: 'measured' }
      : { value: s?.opportunity.value ?? null, basis: 'modeled' };
  const snapKpis: SnapKpi[] = [
    { k: 'Care delivered', b: s ? shown2(s.real, s.measured.basis) : null, v: s?.measured.value != null ? moneyC(s.measured.value) : '–', d: '', dc: '#1F6B52', s: 'documented and billable this month', vc: '#22272E', sp: [], sc: '#54606C' },
    { k: 'Practitioner billed', b: noClaims ? null : measured, v: trend.data && !noClaims ? moneyC(billCur) : '–', d: dBill?.text ?? '', dc: dBill?.up === false ? '#B3261E' : '#1F6B52', s: noClaims ? NO_CLAIMS : 'vs last ' + perWord, vc: noClaims ? '#54606C' : '#22272E', sp: noClaims ? [] : mt.billed, sc: '#2F5C97' },
    {
      k: "Building's share", b: shareKnown ? measured : null, v: !trend.data ? '–' : shareKnown ? moneyC(shareCur) : 'None on file', d: shareKnown ? dShare?.text ?? '' : '', dc: '#1F6B52',
      s: shareKnown ? 'per ' + nAgree + ' agreement' + (nAgree === 1 ? '' : 's') : noClaims || !trend.data ? NO_CLAIMS : (trend.data.totals.slice(-1)[0]?.buildingShareReason ?? 'No agreement on file'), vc: shareKnown ? '#22272E' : '#54606C', sp: shareKnown ? mt.share.map((v) => v ?? 0) : [], sc: '#2F5C97',
    },
    { k: 'Not billed', b: s ? shown2(s.real, notBilledKpi.basis) : null, v: notBilledKpi.value != null ? moneyC(notBilledKpi.value) : '–', d: blocked.length + ' blockers', dc: '#6B4A12', s: 'in Readiness', vc: '#6B4A12', sp: [], sc: '#6B4A12' },
    {
      k: 'Denial rate', b: denCur == null ? null : measured, v: denCur == null ? '–' : denCur.toFixed(1) + '%', d: denCur != null && denPrev != null ? (denCur >= denPrev ? '▲ ' : '▼ ') + Math.abs(denCur - denPrev).toFixed(1) + ' pt' : '', dc: denCur != null && denPrev != null && denCur > denPrev ? '#B3261E' : '#1F6B52',
      s: noClaims ? NO_CLAIMS : 'last ' + perWord + ', your claims', vc: denCur == null ? '#54606C' : '#22272E', sp: noClaims ? [] : mt.denRate.map((v) => v ?? 0), sc: '#B3261E',
    },
    {
      k: 'Days to pay', b: dtp.cur == null ? null : measured, v: dtp.cur == null ? '–' : Math.round(dtp.cur) + ' d',
      d: dDtp == null ? '' : (dDtp > 0 ? '▲ ' : dDtp < 0 ? '▼ ' : '') + Math.abs(dDtp) + ' d', dc: dDtp != null && dDtp > 0 ? '#B3261E' : '#1F6B52',
      s: dtp.slowest ? PAYER_NAME[dtp.slowest.payer] + ' slowest, ' + Math.round(dtp.slowest.days) + ' d' : noClaims || !trend.data ? NO_CLAIMS : 'No paid claims with a remittance date',
      vc: dtp.cur == null ? '#54606C' : '#22272E', sp: dtp.series, sc: '#6B4A12',
    },
  ];

  // Today: the three things that move money soonest, from what the API says now.
  const today: TodayItem[] = [];
  if (consentRows.length) {
    const names = consentRows.slice(0, 2).map((r) => r.resident).join(', ');
    const cents = rRows.filter((r) => consentRows.some((c) => c.residentId === r.residentId)).reduce((a, r) => a + r.cents, 0);
    today.push({
      k: 'c', n: String(today.length + 1), t: consentRows.length === 1 ? 'Get 1 consent' : 'Get ' + consentRows.length + ' consents', subLead: names, sub: consentRows[0].why,
      v: cents ? usd0(cents) + ' / mo' : '', vc: '#B3261E', btn: 'Send consent forms', urgent: true, write: true, go: () => void sendLinks(sendable.map((r) => r.residentId), 'c'),
    });
  }
  const shortest = (qual.data?.residents ?? [])
    .flatMap((r) => r.rows.filter((x) => x.minutes && !x.thresholdMet).map((x) => ({ r, x, togo: x.minutes!.need - x.minutes!.have })))
    .sort((a, b) => a.togo - b.togo)[0];
  if (shortest && qual.data) {
    const days = Math.max(0, Math.ceil((Date.parse(qual.data.closesOn + 'T00:00:00') - now) / 86_400_000));
    const win = wins.data?.wins.find((w) => w.residentId === shortest.r.residentId && w.code === shortest.x.code);
    today.push({
      k: 'm', n: String(today.length + 1), t: 'Log ' + shortest.togo + (shortest.togo === 1 ? ' minute for ' : ' minutes for ') + shortest.r.resident, sub: shortest.x.minutes!.have + ' of ' + shortest.x.minutes!.need + ' min · month closes in ' + days + ' d',
      v: win?.monthlyCents ? usd0(win.monthlyCents) : '', vc: '#6B4A12', btn: 'Log minutes', go: () => goTab('Readiness', shortest.r.residentId),
    });
  }
  if (nearDenial) {
    today.push({
      k: 'a', n: String(today.length + 1), t: 'Appeal ' + (nearDenial.payerName ?? PAYER_NAME[nearDenial.payerClass]) + ' in ' + nearDenial.daysLeft + ' days', subLead: nearDenial.resident ?? undefined, sub: nearDenial.reasons[0]?.plain ?? nearDenial.fix,
      v: usd2(nearDenial.deniedCents), vc: '#1F6B52', btn: 'Open appeal', go: () => goTab('Denials & Underpayments', nearDenial.id),
    });
  }

  const quickList = (r: LensRow) => {
    if (lens === 'code') {
      const items = (qual.data?.residents ?? []).flatMap((x) =>
        x.rows.filter((y) => (y.code ?? '') === r.key && !y.qualified).map((y) => {
          const g = y.stillNeeded[0]?.gate;
          return { a: x.resident + (x.room ? ' · ' + x.room.replace(/^Rm /, '') : ''), b: y.stillNeeded.map((z) => z.text).join(' · ') || y.label, act: g === 'consent' ? 'Get consent' : g === 'minutes' || g === 'device_days' ? 'Log minutes' : g === 'chronic_conditions' ? 'Ask provider' : g === 'care_plan' ? 'Start plan' : g === 'practitioner' ? 'Find practitioner' : 'Readiness' };
        }),
      );
      if (items.length) return { title: 'Blocked residents', items: items.slice(0, 6) };
      // No code-level match (codes hidden, or this code has no qualification rows): the same
      // blockers Readiness and Today count, never "Nothing blocked" beside 140 of them.
      if (blocked.length)
        return { title: 'Blocked residents · all codes', items: blocked.slice(0, 6).map((b) => ({ a: b.who, b: b.blk, act: b.at === 0 ? 'Get consent' : 'Readiness' })) };
      return { title: 'Blocked residents', items: [{ a: 'Nothing blocked', b: oneBuilding ? 'New blockers appear here as claims are drafted' : 'Residents show for one building at a time', act: 'Readiness' }] };
    }
    if (lens === 'payer') {
      const ds = denialList.filter((d) => d.payerClass === r.key);
      return {
        title: 'Open items with this payer',
        items: [
          ...(ds.length ? [{ a: ds.length + ' denial' + (ds.length === 1 ? '' : 's') + ' · ' + usd0(ds.reduce((a, d) => a + d.deniedCents, 0)), b: ds[0].deadlines.text ?? (ds[0].daysLeft != null ? 'Appeal window ' + ds[0].daysLeft + ' d' : ''), act: 'Appeal' }] : []),
          { a: 'Contract', b: r.sub, act: 'Open in Contracts' },
        ],
      };
    }
    if (lens === 'prac') return { title: 'Panel', items: [{ a: 'Agreement', b: r.sub, act: 'Open agreement' }] };
    return { title: 'Blocked residents on this wing', items: [] };
  };
  const best = (r: LensRow) => {
    if (!rates.data) return oneBuilding ? 'Loading published rates…' : 'Choose one building to compare rates.';
    const c = rates.data.codes.find((x) => x.code === r.key);
    const m = c?.market.professional ?? c?.market.institutional;
    if (c && c.market.status === 'published' && m?.medianCents != null)
      return 'Published median ' + usd2(m.medianCents) + (m.p25Cents != null && m.p75Cents != null ? ' · middle half ' + usd2(m.p25Cents) + '–' + usd2(m.p75Cents) : '') + ' · ' + m.count + ' rates.';
    return c?.market.text ?? 'No published commercial rate for this code in your region.';
  };
  const primary = lens === 'payer' || lens === 'prac' ? { t: lens === 'payer' ? 'Open in Contracts' : 'Open agreement', go: () => goTab('Contracts') } : { t: 'Open in Readiness', go: () => goTab('Readiness') };
  const snapEmpty = trend.error ? trend.error.message : lens === 'wing' ? 'Wings are not on the claims yet: each claim needs the resident’s wing before money can be split by wing.' : noClaims ? 'No claims yet. Figures appear after the first claims are paid.' : trend.data ? 'No paid or denied claims in this period.' : '';

  const evs = events.data?.events ?? [];
  const ev = evs.length ? evs[tick % evs.length] : null;
  const liveLine = ev ? { text: (!oneBuilding && ev.facilityName ? ev.facilityName + ' · ' : '') + ev.text, ago: agoOf(ev.at, now) } : null;

  // ── Ask Atlas: answered from the figures this page already loaded ─────────
  const askHit = ((): { head: string; body: string; btn: string; go: () => void } | null => {
    if (ask === 'blk') {
      if (!blocked.length) return { head: 'Nothing is blocking money right now.', body: ready.data ? 'Every resident in scope has what a claim needs.' : CHOOSE, btn: 'Open Readiness', go: () => goTab('Readiness') };
      const by = (at: number) => blocked.filter((r) => r.at === at).length;
      const parts = [
        by(0) && by(0) + (by(0) === 1 ? ' needs consent' : ' need consent'),
        by(1) && by(1) + (by(1) === 1 ? ' has no care plan' : ' have no care plan'),
        by(2) && by(2) + (by(2) === 1 ? ' is missing a diagnosis' : ' are missing a diagnosis'),
        by(3) && by(3) + (by(3) === 1 ? ' is short on minutes' : ' are short on minutes'),
        by(4) && by(4) + (by(4) === 1 ? ' waits on the practitioner' : ' wait on the practitioner'),
      ].filter(Boolean) as string[];
      const days = qual.data ? Math.max(0, Math.ceil((Date.parse(qual.data.closesOn + 'T00:00:00') - now) / 86_400_000)) : null;
      return {
        head: blocked.length + (blocked.length === 1 ? ' claim can’t go out yet.' : ' claims can’t go out yet.'),
        body: (parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts[0]) + (days != null ? ', with ' + days + ' days left in the month.' : '.'),
        btn: 'Open Readiness', go: () => goTab('Readiness'),
      };
    }
    if (ask === 'ren') return { head: 'No renewal dates on file.', body: 'Renewals come from uploaded contracts. Once a contract or remittance is on file, Atlas ranks renewals by money below the published median.', btn: 'Open Contracts', go: () => goTab('Contracts') };
    if (ask === 'apl') {
      if (!nearDenial) return { head: 'Nothing to appeal this week.', body: den.data ? 'No denial has an open appeal window.' : CHOOSE, btn: 'Open Denials', go: () => goTab('Denials & Underpayments') };
      return {
        head: (nearDenial.payerName ?? PAYER_NAME[nearDenial.payerClass]) + ' · ' + (nearDenial.resident ?? 'Resident') + ', ' + nearDenial.daysLeft + ' days left.',
        body: 'Denied as ' + (nearDenial.reasons[0]?.plain ?? nearDenial.reasons[0]?.reason ?? 'not paid').replace(/\.$/, '').toLowerCase() + (nearDenial.code ? ' (' + nearDenial.code + ')' : '') + '. ' + nearDenial.fix + ' Atlas drafts the appeal with the evidence on file. A person reviews and sends it.',
        btn: 'Open Denials', go: () => goTab('Denials & Underpayments', nearDenial.id),
      };
    }
    return null;
  })();

  // ── the list tabs ──────────────────────────────────────────────────────────
  const ui = (t: string): GenUi => ({ filter: gf[t] ?? 'All', sel: gsel[t] ?? null, setFilter: (f) => setGf((x) => ({ ...x, [t]: f })), done });
  const markDone = (id: string) => setDone((d) => ({ ...d, [id]: true }));
  const startOpp = async (g: OppGroup) => {
    if (g.f === 'other') return goTab('Readiness');
    if (g.f === 'consent') {
      const ids = sendable.filter((r) => g.wins.some((w) => w.residentId === r.residentId)).map((r) => r.residentId);
      return void sendLinks(ids, g.id);
    }
    const code = g.wins[0]?.code;
    if (!code) return flash('The code is hidden for this building: ask Aithora to start it.');
    try {
      await captureCodeEnroll(client, { params: { code }, body: { facility, kind: g.f === 'ready' ? 'enroll' : 'flag' } });
      markDone(g.id);
      flash(g.f === 'ready' ? 'Enrollment queued for ' + code : 'Recorded for follow-up: ' + g.act.toLowerCase());
    } catch (e) {
      failed(e);
    }
  };
  const fixReady = async (r: ReadyRow) => {
    if (r.at === 0) return void sendLinks(sendable.some((x) => x.residentId === r.residentId) ? [r.residentId] : [], r.id);
    if (!r.claimId || !r.fixAction) return flash('This step is done on the resident’s record: open the resident to finish it.');
    try {
      await captureAction(client, { body: { action: r.fixAction as CaptureActionKey, claimIds: [r.claimId] } });
      markDone(r.id);
      ready.reload();
    } catch (e) {
      failed(e);
    }
  };
  // The draft on screen (the API's draft as text, or what a person edited), for Review and Edit.
  const draftRef = { text: '' };

  let body: ReactNode = null;
  const railEmpty = (q: { error: { message: string } | null; data: unknown }, fleetOk = false) => (!oneBuilding && !fleetOk ? CHOOSE : q.error && needsBuilding(q.error as never) ? CHOOSE : q.error ? q.error.message : q.data ? 'Pick a row to see it here.' : '');
  const genProps = { tag, narrow, railOpen, setRailOpen, roReason: writeOff };
  if (tab === 'Snapshot') {
    body = (
      <Snapshot
        kpis={snapKpis}
        today={today}
        done={done}
        rows={rows}
        lens={lens}
        setLens={setLens}
        pt={pt}
        setPt={setPt}
        per={per}
        setPer={setPer}
        quickList={quickList}
        best={best}
        primary={primary}
        askWhy={(r) => openHelp('Why is ' + r.name + ' flagged in Capture' + (r.sig ? ': ' + r.sig.text : '') + '?')}
        measured={measured}
        modeled={modeled}
        delivered={s ? shown2(s.real, s.measured.basis) : null}
        tagSuffix={tagSuffix}
        emptyText={snapEmpty}
        live={liveLine}
        {...genProps}
      />
    );
  } else if (tab === 'Contracts' && effScope === 'net') {
    const d = network(ui('Network'));
    body = <GenTab label="Network" d={d} sel={null} onSel={() => undefined} railEmpty="No network yet. Each building's readiness shows here once the network is set up." {...genProps} />;
  } else if (tab === 'Contracts') {
    body = (
      <Contracts
        rates={rates.data}
        error={!oneBuilding ? CHOOSE : rates.error ? (needsBuilding(rates.error) ? CHOOSE : rates.error.message) : null}
        scope={effScope === 'port' ? 'port' : 'bld'}
        now={now}
        access={fa ? { pending: reqSent || (accessReqs.data?.requests.length ?? 0) > 0, off: writeOff, go: () => void codeAccessRequest(client, { body: {} }).then(() => setReqSent(true), failed) } : null}
        {...genProps}
      />
    );
  } else {
    let d;
    let label = tab as string;
    let draftSlot: ReactNode | undefined;
    let banner: ReactNode | undefined;
    let toolbar: ReactNode | undefined;
    if (tab === 'Opportunity') {
      d = opportunity(wins.data, ui(tab), { start: (g) => void startOpp(g), ask: () => openHelp('Which unbilled code is easiest to start this month?') }, tagSuffix);
      if (!oneBuilding || (wins.error && needsBuilding(wins.error))) d = { ...d, empty: CHOOSE };
      else if (wins.error) d = { ...d, empty: wins.error.message };
      d = withPractitioners(d, pracs.data, ui(tab), {
        step: async (p) => {
          const a = p.nextStep ? PRAC_ACTIONS[p.nextStep] : null;
          if (!a) return;
          try {
            await capturePractitionerAction(client, { params: { id: p.id }, body: { action: a } });
            markDone(p.id);
          } catch (e) {
            failed(e);
          }
        },
      }, tagSuffix);
      if ((gf[tab] ?? 'All') === 'Practitioner enrollment' && pracs.error) d = { ...d, empty: pracs.error.message };
      if (fa && masked) {
        const pending = reqSent || (accessReqs.data?.requests.length ?? 0) > 0;
        banner = (
          <div data-testid="capture-code-access" style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px;padding:9px 12px;border-radius:8px;border:1px solid rgba(47,92,151,.3);background:rgba(47,92,151,.06);')}>
            <span style={sx('flex:1 1 320px;min-width:0;font-size:16px;line-height:1.4;color:#22272E;')}>Codes are hidden for this building. Every total and rate is yours to see; code-level lines come with the Capture plan.</span>
            {pending ? (
              <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>Requested · Aithora confirms within one business day</span>
            ) : (
              <a
                href="#"
                aria-disabled={writeOff ? 'true' : undefined}
                title={writeOff ?? undefined}
                onClick={(e) => {
                  pre(e);
                  if (writeOff) return;
                  codeAccessRequest(client, { body: {} }).then(() => setReqSent(true), failed);
                }}
                style={sx(PRI + (writeOff ? 'opacity:.55;cursor:not-allowed;' : ''))}
              >
                Request code-level access
              </a>
            )}
          </div>
        );
      }
    } else if (tab === 'Forecast') {
      d = forecast(
        fc.data, fcBase.data,
        { ...ui(tab), uptake, setUptake },
        {
          pick: (k) => { setScenario(k); setGsel((x) => ({ ...x, Forecast: k })); },
          exportIt: () => {
            const h = fc.data?.horizon;
            if (!h) return;
            download('capture-forecast.csv', [csvLine(['months', 'from', 'through', 'low', 'expected', 'high', 'basis']), ...h.horizons.map((x) => csvLine([x.months, x.from, x.through, ((x.low.practitioner.value ?? 0) / 100).toFixed(2), ((x.expected.practitioner.value ?? 0) / 100).toFixed(2), ((x.high.practitioner.value ?? 0) / 100).toFixed(2), 'MODELED']))].join('\n'), 'text/csv');
          },
        },
        tagSuffix,
      );
      if (fc.error) d = { ...d, empty: needsBuilding(fc.error) ? CHOOSE : fc.error.message };
      label = 'Forecast';
    } else if (tab === 'Readiness') {
      d = readiness(ready.data, qual.data, s, ui(tab), now, { fix: (r) => void fixReady(r), sendAll: () => void sendLinks(sendable.map((x) => x.residentId), 'all-consent'), consentCount: sendable.length }, tagSuffix);
      if (ready.error) d = { ...d, empty: ready.error.message };
      d = withBatches(d, batches.data, ui(tab), {
        resend: async (b) => {
          if (!b.batchId) return;
          try {
            await captureBatchResend(client, { params: { batchId: b.batchId }, body: { confirm: true } });
            markDone(b.batchId);
            batches.reload();
          } catch (e) {
            failed(e);
          }
        },
      }, !oneBuilding, tagSuffix);
      if ((gf[tab] ?? 'All') === 'At clearinghouse' && batches.error) d = { ...d, empty: batches.error.message };
      banner = <ClaimsToSign />;
      const readyIds = s?.pipeline?.readyClaimIds ?? [];
      if (signSent != null) toolbar = <span role="status" style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>{signSent + ' sent to the practitioner for signature'}</span>;
      else if (readyIds.length)
        toolbar = (
          <a
            href="#"
            aria-disabled={writeOff ? 'true' : undefined}
            title={writeOff ?? 'Routes them to the enrolled practitioner to sign. Aithora never submits a claim.'}
            onClick={(e) => {
              pre(e);
              if (writeOff) return;
              captureSendForSignature(client, { body: { claimIds: readyIds } }).then((r) => { setSignSent(r.routed.length); summary.reload(); }, failed);
            }}
            style={sx(PRI + (writeOff ? 'opacity:.55;cursor:not-allowed;' : ''))}
          >
            {'Send ' + readyIds.length + ' ready claims to the practitioner'}
          </a>
        );
    } else if (tab === 'Denials & Underpayments') {
      d = denials(den.data, draft.data, ui(tab), {
        review: (x: CaptureDenial) => download('appeal-draft-' + x.id + '.txt', editDraft ?? draftRef.text),
        edit: () => setEditDraft((v) => (v == null ? draftRef.text : null)),
      }, tagSuffix);
      draftRef.text = d.rail?.draft ?? '';
      if (editDraft != null && d.rail) {
        draftSlot = (
          <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
            <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>Atlas draft · a person reviews and sends</span>
            <textarea
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              aria-label="Atlas draft"
              rows={6}
              style={sx('font-family:Archivo,sans-serif;font-size:14px;line-height:1.45;padding:8px 10px;border-radius:7px;border:1px dashed rgba(34,39,46,.25);background:#FFFFFF;color:#22272E;resize:vertical;')}
            />
          </div>
        );
      }
      if (!oneBuilding || (den.error && needsBuilding(den.error))) d = { ...d, empty: CHOOSE };
      else if (den.error) d = { ...d, empty: den.error.message };
      label = 'Denials';
    } else {
      d = auditPack(audit.data, consent.data ? consentRows.length : null, ui(tab), {
        fix: (f) => {
          if (f.risk === 'Low') download('audit-flags.txt', (audit.data?.rule ?? '') + '\n\n' + (audit.data ? auditText(audit.data) : ''));
          else goTab('Readiness');
        },
      }, tagSuffix);
      if (!oneBuilding || (audit.error && needsBuilding(audit.error))) d = { ...d, empty: CHOOSE };
      else if (audit.error) d = { ...d, empty: audit.error.message };
      label = 'Audit Pack';
    }
    const filt = gf[tab] ?? 'All';
    const q = tab === 'Opportunity' ? (filt === 'Practitioner enrollment' ? pracs : wins) : tab === 'Readiness' && filt === 'At clearinghouse' ? batches : tab === 'Forecast' ? fc : tab === 'Readiness' ? ready : tab === 'Denials & Underpayments' ? den : audit;
    body = (
      <GenTab
        label={label}
        d={d}
        sel={tab === 'Forecast' ? gsel.Forecast ?? fc.data?.horizon?.scenario ?? null : gsel[tab] ?? null}
        onSel={(id) => setGsel((x) => ({ ...x, [tab]: id }))}
        draftSlot={draftSlot}
        banner={banner}
        toolbar={toolbar}
        railEmpty={railEmpty(q as never, q === pracs || q === batches)}
        {...genProps}
      />
    );
  }

  // Tab counts (design pvTabs): codes to start, claims blocked, denials open.
  // A denial counts while it can still be appealed (an expired or final one is history).
  const openDenials = denialList.filter((d) => d.appealable && d.recovery?.rank !== 'closed').length;
  const counts: Partial<Record<CaptureTab, number>> = { Opportunity: groups.length, Readiness: blocked.length, 'Denials & Underpayments': openDenials };
  const scopeChips: [Scope, string][] = operator ? [['bld', 'This building'], ['port', 'Portfolio'], ['net', 'Network']] : [['bld', 'This building']];

  return (
    <div data-screen-label="Capture" data-testid="capture" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:10px;min-width:0;')}>
      {/* The tabs wrap to a second line rather than scroll: a tab is never cut ("pportunity"). */}
      <div data-testid="capture-tabs-row" style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;white-space:nowrap;gap:6px 8px;')}>
        <div role="tablist" aria-label="Capture" data-testid="capture-tabs" style={sx('flex:0 0 auto;min-width:0;display:flex;flex-wrap:nowrap;gap:2px;align-items:center;')}>
          {TABS.filter((t) => DAILY.includes(t)).map((t) => (
            <a key={t} href="#" role="tab" aria-selected={tab === t} onClick={(e) => { pre(e); setTab(t); }} style={sx(pvTab(tab === t))}>
              {t}
              {counts[t] ? <span style={sx('margin-left:5px;font-size:14px;font-weight:700;color:' + (TAB_C[t] ?? '#54606C') + ';')}>{counts[t]}</span> : null}
            </a>
          ))}
          <MoreMenu
            testId="capture-more"
            minWidth={240}
            items={TABS.filter((t) => !DAILY.includes(t)).map((t) => ({ id: t, label: t, count: counts[t] ?? null, countColor: TAB_C[t] }))}
            active={tab}
            onPick={(t) => setTab(t)}
            tabStyle={(on) => 'flex:0 0 auto;' + pvTab(on)}
          />
        </div>
        {/* This building / Portfolio / Network: Operator only. A Facility Admin has the one building, so no chip (V7 file). */}
        {tab === 'Contracts' && scopeChips.length > 1 && (
          <span style={sx('flex:0 0 auto;display:inline-flex;gap:2px;padding:2px;border-radius:8px;border:1px solid rgba(34,39,46,.15);margin-left:6px;')}>
            {scopeChips.map(([k, l]) => (
              <a key={k} href="#" onClick={(e) => { pre(e); setCscope(k); }} style={sx(scopeChip(effScope === k))} aria-pressed={effScope === k}>{l}</a>
            ))}
          </span>
        )}
        <span style={sx('flex:1 1 10px;')} />
        {scopeWords && (
          <span data-testid="capture-scope" style={sx('flex:0 0 auto;font-size:14px;font-weight:700;color:#3C454F;')}>
            <span>Showing</span> <span translate={pickOne ? 'no' : undefined}>{scopeWords}</span>
          </span>
        )}
        {note && <span role="status" style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>{note}</span>}
        {!canAct && <span data-testid="capture-readonly" style={sx('flex:0 0 auto;font-size:14px;color:#54606C;')}>{'Read-only as ' + tierLabel(role)}</span>}
        {pageSample && buildLabels && shown('SAMPLE') && (
          <span style={sx("font-family:'IBM Plex Mono',monospace;font-size:14px;font-weight:600;padding:2px 7px;border-radius:4px;background:rgba(34,39,46,.08);color:#54606C;")}>SAMPLE</span>
        )}
      </div>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:6px 8px;')}>
        <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#1D3F6B;')}>ASK ATLAS</span>
        {([['blk', 'What’s blocking money?'], ['ren', 'Which renewal matters most?'], ['apl', 'What can I appeal this week?']] as const).map(([k, l]) => (
          <a key={k} href="#" onClick={(e) => { pre(e); setAsk((a) => (a === k ? null : k)); }} style={sx(askChip(ask === k))} aria-pressed={ask === k}>{l}</a>
        ))}
        <span style={sx('font-size:14px;color:#54606C;')}>or say “Hey Siri, ask Aithora what’s blocking money”</span>
      </div>
      {askHit && (
        <div data-screen-label="Capture Atlas answer" role="region" aria-label="Atlas answer" style={sx('flex:0 0 auto;display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;padding:12px 14px;border-radius:10px;background:#FFFFFF;border:1px solid rgba(47,92,151,.4);')}>
          <div style={sx('flex:1 1 320px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
            <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#1D3F6B;')}>{'ATLAS · FROM YOUR CLAIMS' + tagSuffix}</span>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{askHit.head}</span>
            <span style={sx('font-size:16px;line-height:1.45;color:#3C454F;text-wrap:pretty;')}>{askHit.body}</span>
          </div>
          <div style={sx('flex:0 0 auto;display:flex;gap:8px;align-items:center;')}>
            <a href="#" onClick={(e) => { pre(e); askHit.go(); setAsk(null); }} style={sx('display:inline-flex;align-items:center;min-height:34px;padding:0 14px;border-radius:7px;background:#22272E;color:#F6F3EC;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>{askHit.btn}</a>
            <a href="#" onClick={(e) => { pre(e); setAsk(null); }} style={sx('display:inline-flex;align-items:center;min-height:34px;padding:0 10px;border-radius:7px;color:#3C454F;font-size:16px;font-weight:700;text-decoration:none;')}>Close</a>
          </div>
        </div>
      )}
      {body}
    </div>
  );
}

/** A summary figure's tag: SAMPLE when the response is not real, else the figure's basis. */
function shown2(real: boolean, basis: string): Honesty {
  if (!real) return 'SAMPLE';
  return basis === 'modeled' ? 'MODELED' : basis === 'sample' ? 'SAMPLE' : 'MEASURED';
}

function auditText(a: NonNullable<Parameters<typeof auditPack>[0]>): string {
  return a.practitioners
    .map((p) => (p.practitioner ?? p.npi ?? 'Practitioner') + ' · ' + (p.code ?? 'code hidden') + ' · ' + p.level + '\n' + p.measures.map((m) => '  ' + m.label + ': ' + (m.value ?? '–') + (m.national != null ? ' (national ' + m.national + ')' : '') + (m.why ? ' · ' + m.why : '')).join('\n'))
    .join('\n\n');
}

/** "just now", "4 min ago", "3 h ago", "2 d ago". */
function agoOf(iso: string, now: number): string {
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60000));
  return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : m < 1440 ? Math.round(m / 60) + ' h ago' : Math.round(m / 1440) + ' d ago';
}
